/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { EmitterMixin } from 'ckeditor5/src/utils.js';

import MessagesCompressor from './../messagescompressor';

import RevisionHistoryGetRevisionMessage from './messages/revisionhistorygetrevisionmessage';
import RevisionHistoryGetRevisionResponse from './responses/revisionhistorygetrevisionresponse';
import RevisionHistoryUpdateRevisionsMessage from './messages/revisionhistoryupdaterevisionsmessage';
import RevisionHistoryUpdateRevisionsResponse from './responses/revisionhistoryupdaterevisionsresponse';
import RevisionHistoryConnectMessage from './messages/revisionhistoryconnectmessage';
import RevisionHistoryConnectResponse from './responses/revisionhistoryconnectresponse';
import RevisionHistoryReconnectMessage from './messages/revisionhistoryreconnectmessage';

import CKEditorCloudServicesError from '../ckeditorcloudserviceserror';
import WebSocketGateway, {
	IWebSocketGateway,
	WebSocketGatewayState,
	WEB_SOCKET_GATEWAY_STATES
} from '../websocketgateway/websocketgateway';
import { IRevisionObject } from './revision';
import Channel from '../websocketgateway/channel';
import ServiceNotConnectedError from '../errors/ServiceNotConnectedError';

import type Message from '../message';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SERVICE: number = 12;

interface IRevisionHistoryConnectResponse {
	revisions: IRevisionObject[];
	requestId: number;
}

// eslint-disable-next-line new-cap
class RevisionHistoryService extends /* #__PURE__ -- @preserve */ EmitterMixin() {
	private _isConnected: boolean = false;

	private _wsGateway?: IWebSocketGateway;

	private _channel?: Channel;

	private static readonly _SERVICE: number = _SERVICE;

	public constructor( private readonly _documentId: string ) {
		super();
	}

	public get isConnected(): boolean {
		return this._isConnected;
	}

	/**
	 * Connects RevisionHistoryService to CKEditor Cloud Services.
	 *
	 * @throws {@link CKEditorCloudServicesError} error
	 * Thrown in the case of errors on the backend side.
	 */
	public connect( wsGateway: IWebSocketGateway ): Promise<IRevisionHistoryConnectResponse | void> {
		if ( this._isConnected ) {
			return Promise.resolve();
		}

		return this._connect( wsGateway, new RevisionHistoryConnectMessage( this._documentId ) );
	}

	/**
	 * Disconnects RevisionHistoryService from CKEditor Cloud Services.
	 *
	 * @throws {@link CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public disconnect(): void {
		if ( !this._isConnected ) {
			return;
		}

		this._isConnected = false;

		if ( this._wsGateway ) {
			this.stopListening( this._wsGateway );

			this._wsGateway = undefined;
		}

		if ( this._channel ) {
			this.stopListening( this._channel );

			this._channel = undefined;
		}

		this.fire( 'disconnected' );

		this.stopListening();
	}

	/**
	 * Reconnects RevisionHistoryService to CKEditor Cloud Services.
	 *
	 * @throws {@link CKEditorCloudServicesError}
	 * Thrown when connection already exists or in the case of errors on the backend side.
	 */
	public reconnect( wsGateway: IWebSocketGateway, lastRequestId: number ): Promise<IRevisionHistoryConnectResponse> {
		if ( this.isConnected ) {
			throw new CKEditorCloudServicesError( 'Cannot reconnect to already connected service.', wsGateway );
		}

		return this._connect( wsGateway, new RevisionHistoryReconnectMessage( this._documentId, lastRequestId ) );
	}

	/**
	 * Updates revisions.
	 *
	 * @throws {@link CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public async updateRevisions( revisions: IRevisionObject[], lastRequestId: number ): Promise<number> {
		const message: RevisionHistoryUpdateRevisionsMessage = RevisionHistoryUpdateRevisionsMessage.create(
			{
				documentId: this._documentId,
				requestId: lastRequestId,
				revisions
			}
		);

		const responseBuffer: Buffer = await this._sendRequest( RevisionHistoryUpdateRevisionsMessage.TYPE, message );

		const { requestId } = MessagesCompressor.decode(
			responseBuffer,
			RevisionHistoryUpdateRevisionsResponse
		) as RevisionHistoryUpdateRevisionsResponse;

		return requestId;
	}

	/**
	 * Returns revision with given id.
	 *
	 * @throws {@link CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public async getRevision( revisionId: string ): Promise<IRevisionObject> {
		const message: RevisionHistoryGetRevisionMessage = new RevisionHistoryGetRevisionMessage( this._documentId, revisionId );

		const responseBuffer: Buffer = await this._sendRequest( RevisionHistoryGetRevisionMessage.TYPE, message );

		return ( MessagesCompressor.decode( responseBuffer, RevisionHistoryGetRevisionResponse ) as RevisionHistoryGetRevisionResponse )
			.toObject();
	}

	/**
	 * Connects or reconnects to WebSocketGateway and starts listening on channel.
	 */
	private async _connect(
		wsGateway: IWebSocketGateway,
		message: RevisionHistoryConnectMessage | RevisionHistoryReconnectMessage
	): Promise<IRevisionHistoryConnectResponse> {
		if ( wsGateway.state !== WEB_SOCKET_GATEWAY_STATES.CONNECTED ) {
			throw new CKEditorCloudServicesError( 'WebSocket Gateway is not connected.', wsGateway );
		}

		this._wsGateway = wsGateway;
		this.stopListening( wsGateway, 'change:state' );

		const serviceResponse: Buffer = await wsGateway._sendRequest(
			_SERVICE,
			message.constructor.TYPE as string,
			MessagesCompressor.encode( message )
		);

		const { channel, requestId, revisions } = ( MessagesCompressor.decode(
			serviceResponse,
			RevisionHistoryConnectResponse
		) as RevisionHistoryConnectResponse ).toObject();

		this.listenTo(
			wsGateway,
			'change:state',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			( _0, _1, state ) => this._onWsGatewayStateChange( state as WebSocketGatewayState ),
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		this._connectToChannel( wsGateway, channel );

		this._isConnected = true;
		this.fire( 'connected' );

		return {
			revisions,
			requestId
		};
	}

	/**
	 * Starts to listen on a given channel.
	 */
	private _connectToChannel( wsGateway: IWebSocketGateway, channelName: string ): void {
		this._channel = wsGateway._getChannel( RevisionHistoryService._SERVICE, channelName );

		if ( !this._channel ) {
			return;
		}

		this.listenTo(
			this._channel,
			this._channel.getEventName( RevisionHistoryUpdateRevisionsMessage.TYPE ),
			( event, data ) => {
				const { documentId, requestId, revisions } = (
					MessagesCompressor.decode( data, RevisionHistoryUpdateRevisionsMessage ) as RevisionHistoryUpdateRevisionsMessage
				).toObject();

				this.fire( 'revisionsUpdated', {
					documentId,
					requestId,
					revisionsData: revisions
				} );
			}
		);
	}

	/**
	 * Processes websocket gateway state changes.
	 */
	private _onWsGatewayStateChange( value: WebSocketGatewayState ): void {
		if ( value === WEB_SOCKET_GATEWAY_STATES.DISCONNECTED ) {
			this.disconnect();
		}
	}

	/**
	 * Sends request to RevisionHistory service.
	 *
	 * @throws {@link ServiceNotConnectedError}
	 * Thrown in the case of missing service connection.
	 */
	private _sendRequest( method: string, message: Message ): Promise<Buffer> {
		if ( !this._wsGateway || !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Revision History', this );
		}

		return this._wsGateway._sendRequest( _SERVICE, method, MessagesCompressor.encode( message ) );
	}
}

export default RevisionHistoryService;
